import { Children, Fragment, ReactNode } from 'react';

import { HistoryIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { StatusDot } from '@/components/ui/custom/StatusDot';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { Member } from '@/shared/types/member';
import { timeFromNow } from '@/util/dateUtils';

interface ResourceTileStatusProps {
  label: string;
  /** `successful` for a live item, `neutral` for an off item or a plain category. */
  flavour: 'successful' | 'neutral';
}

/** Dot and label beside a tile's title, matching the Connections and Rules status. */
export const ResourceTileStatus = ({ label, flavour }: ResourceTileStatusProps) => (
  <div className="flex shrink-0 items-center gap-1">
    <StatusDot aria-hidden="true" variant={flavour} />
    <span
      className={cn(
        'text-xs leading-4 font-normal',
        flavour === 'successful' ? 'text-success' : 'text-muted-foreground'
      )}
    >
      {label}
    </span>
  </div>
);

export const ActiveStatus = ({ active }: { active: boolean }) => {
  const { t } = useTranslation();

  return (
    <ResourceTileStatus
      label={active ? t('common.active') : t('common.inactive')}
      flavour={active ? 'successful' : 'neutral'}
    />
  );
};

/** The muted line under a tile's title: its children joined with a dot. */
export const ResourceTileMeta = ({ children }: { children: ReactNode }) => (
  <span className="flex min-w-0 items-center gap-1.5 text-sm leading-5 font-normal text-muted-foreground">
    {Children.toArray(children).map((child, index) => (
      // Children.toArray has already keyed each child; the Fragment needs its own key.
      <Fragment key={(child as { key?: string }).key ?? index}>
        {index > 0 && <span aria-hidden="true">·</span>}
        {child}
      </Fragment>
    ))}
  </span>
);

export const CreatorMeta = ({ member }: { member: Member }) => (
  <span className="flex min-w-0 items-center gap-1.5">
    {/* Re-enable hover so the avatar tooltip shows. */}
    <span className="pointer-events-auto relative flex shrink-0">
      <Avatar userId={member.user_id} size="small" hideStatus />
    </span>
    <span className="min-w-0 truncate">{member.name}</span>
  </span>
);

export const LastUsedMeta = ({ lastUsedAt }: { lastUsedAt: string | null | undefined }) => {
  const { t } = useTranslation();

  return (
    <span className="flex shrink-0 items-center gap-1">
      <HistoryIcon aria-hidden="true" size={16} />
      {lastUsedAt ? (
        <TimestampTooltip timestamp={lastUsedAt}>
          {/* The meta line lets clicks through to the tile; the timestamp still takes hover. */}
          <span className="pointer-events-auto relative">{timeFromNow(lastUsedAt)}</span>
        </TimestampTooltip>
      ) : (
        <span>{t('common.not_used_yet')}</span>
      )}
    </span>
  );
};

interface ResourceActiveSwitchProps {
  /** The item's name, for the switch's accessible label. */
  name: string;
  checked: boolean;
  onCheckedChange: () => void;
  disabled: boolean;
}

export const ResourceActiveSwitch = ({
  name,
  checked,
  onCheckedChange,
  disabled,
}: ResourceActiveSwitchProps) => {
  const { t } = useTranslation();

  return (
    <Switch
      aria-label={t('developer_tools.active_toggle', { 0: name })}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
    />
  );
};
